import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip
} from "@mui/material";
import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { createProject } from "../../../utils/api";
import { BIOBANK_ROUTES } from "../../../constants/routes";
import { useAuth } from "../../../components/AuthProvider";

const AddProjectForm = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    projectName: "",
    irbNum: "",
    description: "",
    objective: "",
    fundingSource: "",
    piName: "",
    institution: "",
    collaborators: [],
  });

  const [currentCollaborator, setCurrentCollaborator] = useState("");
  const [errors, setErrors] = useState({});

  const handleAddCollaborator = () => {
    if (currentCollaborator.trim() !== "") {
      setFormData(prev => ({
        ...prev,
        collaborators: [...prev.collaborators, currentCollaborator.trim()]
      }));
      setCurrentCollaborator("");
    }
  };

  const handleRemoveCollaborator = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      collaborators: prev.collaborators.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  const validateFields = () => {
    const newErrors = {};

    if (!formData.projectName) newErrors.projectName = "Project Name is required";

    return newErrors;
  };

  const handleContinue = async () => {
    if (!isAdmin) return;
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const payload = {
        ...formData
      };

      await createProject(payload);
      navigate(BIOBANK_ROUTES.PROJECT_MANAGEMENT);
    } catch (error) {
      console.error(error);
      setErrors({ submit: "Failed to create project. Please try again." });
    }
  };

  if (!isAdmin) return <Navigate to={BIOBANK_ROUTES.PROJECT_MANAGEMENT} replace />;

  return (
    <FocusedFlowPage
      title="Create New Project"
      onClose={() => navigate(BIOBANK_ROUTES.PROJECT_MANAGEMENT)}
      maxWidth={760}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={() => navigate(BIOBANK_ROUTES.PROJECT_MANAGEMENT)}
          sx={{
            borderRadius: 0,
            borderColor: "#B28F38",
            color: "var(--tamu-maroon)",
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#fff8e1",
              borderColor: "#B28F38",
            },
          }}
        >
          ← BACK
        </Button>,
        <Button
          key="create"
          variant="contained"
          onClick={handleContinue}
          sx={{
            backgroundColor: "var(--tamu-maroon)",
            borderRadius: 0,
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "var(--tamu-maroon)",
            },
          }}
        >
          CREATE PROJECT →
        </Button>,
      ]}
    >
      <Box sx={{ maxWidth: "700px", margin: "0 auto", padding: "0rem 0" }}>
        <Stack spacing={3}>
          {errors.submit && (
            <Typography sx={{ color: "#d32f2f", fontSize: "14px", textAlign: "center" }}>
              {errors.submit}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Project Name"
                required
                value={formData.projectName}
                onChange={handleChange("projectName")}
                error={errors.projectName}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="IRB Number"
                value={formData.irbNum}
                onChange={handleChange("irbNum")}
                error={errors.irbNum}
              />
            </Box>
          </Box>

          <Field
            label="Description"
            value={formData.description}
            onChange={handleChange("description")}
            error={errors.description}
            multiline
            rows={3}
          />

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Objective"
                value={formData.objective}
                onChange={handleChange("objective")}
                error={errors.objective}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Funding Source"
                value={formData.fundingSource}
                onChange={handleChange("fundingSource")}
                error={errors.fundingSource}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Field
                label="PI Name"
                value={formData.piName}
                onChange={handleChange("piName")}
                error={errors.piName}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Field
                label="Institution"
                value={formData.institution}
                onChange={handleChange("institution")}
                error={errors.institution}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#3E3E3E", mb: "6px" }}>
              Collaborators
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={currentCollaborator}
                onChange={(e) => setCurrentCollaborator(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCollaborator();
                  }
                }}
                sx={{
                  input: { padding: "10px", fontSize: "16px", backgroundColor: "white", borderRadius: "6px" }
                }}
              />
              <Button 
                variant="contained" 
                onClick={handleAddCollaborator}
                sx={{ 
                  backgroundColor: '#500000', 
                  color: 'white', 
                  height: '43px',
                  px: 3,
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#3a0000' }
                }}
              >
                Add
              </Button>
            </Box>
            {formData.collaborators.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.collaborators.map((collaborator, index) => (
                  <Chip
                    key={index}
                    label={collaborator}
                    onDelete={() => handleRemoveCollaborator(index)}
                    sx={{ backgroundColor: '#f5f5f5', fontWeight: 500 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </FocusedFlowPage>
  );
};

const Field = ({
  label,
  required,
  value,
  onChange,
  error,
  type = "text",
  placeholder,
  multiline,
  rows,
}) => (
  <Box>
    <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#3E3E3E", mb: "6px" }}>
      {label}
      {required && <span style={{ color: "red", marginLeft: "3px" }}>*</span>}
    </Typography>
    <TextField
      fullWidth
      type={type}
      value={value}
      onChange={onChange}
      error={!!error}
      helperText={error}
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      InputLabelProps={{ shrink: true }}
      sx={{
        input: {
          padding: "10px",
          fontSize: "16px",
          backgroundColor: "white",
          borderRadius: "6px",
        },
        textarea: {
          padding: "10px",
          fontSize: "16px",
          backgroundColor: "white",
          borderRadius: "6px",
        },
      }}
    />
  </Box>
);

export default AddProjectForm;
